import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { OPEN_ISSUE_PARAM } from '@constants/activitiesParams';
import { issueItemKey, type IListItem } from '../model/listItem';

/**
 * The open issue lives in the URL as `?openIssue=<issueId>`. The URL is the
 * single source of truth — no Redux mirror, no `useState`, no sync loop.
 * (Activities open the shared TaskPanel instead — see `useActivityPanel`.)
 *
 *   useIssueRoute()      — write-only. open(key) / close(). `key` is the
 *                          list item's `iss:<id>`. Used by cards and chips.
 *   useOpenIssue(items)  — reader. Resolves the URL param against the loaded
 *                          list, returns the matching IListItem (or null).
 *                          Used by the page that renders the drawer.
 */
export function useIssueRoute() {
  const [, setSearchParams] = useSearchParams();

  const open = useCallback(
    (key: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(OPEN_ISSUE_PARAM, key.replace(/^iss:/, ''));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const close = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(OPEN_ISSUE_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return { open, close };
}

export function useOpenIssue(items: IListItem[]) {
  const [searchParams] = useSearchParams();
  const { close } = useIssueRoute();
  const issueId = searchParams.get(OPEN_ISSUE_PARAM);

  const openItem = useMemo<IListItem | null>(
    () => (issueId ? items.find((item) => item.key === issueItemKey(issueId)) ?? null : null),
    [issueId, items],
  );

  return { openItem, close };
}
